import { useQueries } from '@tanstack/react-query';
import { queryOptionsGetInvoicesPaginated } from '../../../../api/payments/invoiceApi';
import { queryOptionsGetStatementsPaginated } from '../../../../api/payments/statementApi';
import { queryOptionsGetTitlesPaginated } from '../../../../api/titles/titlesApi';
import {
  type FilterTab,
  VISIBLE_TABS,
  firstTabListPage,
  tabInvoicesParams,
  tabStatementsParams,
  usesStatementsApi,
} from '../../Title/Summary/statementFilterTabs';

interface ClientStatementTabs {
  /**
   * The tab to open on when none has been picked: the first enabled tab with
   * listed records, so the view opens on rows whenever there are any, else
   * the first enabled tab.
   */
  defaultTab?: FilterTab;
  /**
   * The visible tabs to show for this client, in display order, by the rule
   * below. Undefined until every check has come back once, so tabs never
   * appear one at a time.
   */
  enabledTabs?: FilterTab[];
  /**
   * The first records check's error, once every records check has failed:
   * every tab's table lists from those same APIs, so none of them could load.
   */
  error: Error | null;
}

/**
 * Which Statements tabs apply to a client. A tab shows when the client is
 * registered for any of its services, or already has a listed record under
 * it, so a client who is registered but hasn't been paid yet still gets the
 * tab, and its table opens on its own empty state. Each visible tab gets two
 * checks.
 *
 * Registration: one title (`pageSize: 1`) in this workspace that the client
 * owns (`ownerClientIds`) and whose `serviceType` includes any of the tab's
 * services. That is the rule Title Summary uses for its own tabs
 * (`title.serviceType`), applied across every title the client owns. `Client`
 * has no service fields, so the client's titles are the only per-client
 * registration record the frontend can query. Every title level counts except
 * Royalties Seasons, which `/v2/title` never returns
 * (`excludeRoyaltiesSeasons`), so an episode the client owns is enough. Every
 * claim status counts too, Withdrawn, Expired rights and Draft included:
 * which statuses mean "registered" is still open with product.
 *
 * That check is only as good as the data behind it. The MSR2 registration
 * handler is the only production write path for a title's `serviceType` in
 * this repo, and it writes royalties types only; `collectionAccount`,
 * `disbursements` and `residuals` reach a title only from data loaded outside
 * this repo (and from local seed data, so a local run proves nothing about
 * them). Those two tabs also list by beneficiary, not owner: a client who is
 * a DAS, CAM or Residuals beneficiary on another owner's title reads as not
 * registered until its first paid invoice. Reading registration from
 * `Beneficiary` instead needs a `beneficiaryClientId` filter on
 * `GET /beneficiary`, which the backend doesn't have yet.
 *
 * Separate counts rather than reading `serviceType` off the page of titles
 * `ClientTasksSection` already fetches: a count is exact however many titles
 * the client has, in one round trip, and doesn't depend on another section's
 * page size.
 *
 * Records: the exact first page the tab's `StatementsDataTable` opens on
 * (same params from `tabStatementsParams`/`tabInvoicesParams`, page 1, no
 * search, newest first), counting as found when that page reports any rows.
 * It covers a client paid under a service without owning a title tagged with
 * it: CAM/DAS pays one owner's collections out to other beneficiaries, and
 * statement beneficiaries can be corrected after import, so an incomplete
 * registration record never hides money the client was paid. Sharing the
 * request means the table for whichever tab is open finds its first page
 * already cached, rather than making a second request behind a second
 * skeleton. A committed search, sort or page in the URL is a different
 * request, so the check deliberately ignores them: a tab stays put while you
 * search within it.
 *
 * A failed check says nothing either way, so its tab follows the tab's other
 * check: shown if that one found something, hidden if it found nothing. Only
 * when both of a tab's checks fail does the tab show regardless (fail open),
 * and its table then retries the records request itself. When every records
 * check fails, the error replaces every tab, whatever registration found,
 * because each tab's table lists from those same APIs.
 *
 * Holds off until `workspaceId` is known, so it never checks across
 * workspaces. The checks don't poll. Each records check shares its entry with
 * that tab's table, so whichever table is open keeps its own entry fresh; the
 * registration checks are fetched once per mount or client. Tab membership
 * needn't track either live.
 */
export const useClientStatementTabs = (
  clientId: string,
  workspaceId?: string,
): ClientStatementTabs =>
  useQueries({
    combine: (results): ClientStatementTabs => {
      type Check = (typeof results)[number];

      const hasFailed = ({ data, isError }: Check) => isError && !data;
      const finds = ({ data }: Check) => (data?.meta?.totalItems ?? 0) > 0;

      const records = results.slice(0, VISIBLE_TABS.length);
      const registrations = results.slice(VISIBLE_TABS.length);
      const recordFailures = records.filter(hasFailed);
      const error =
        recordFailures.length === records.length
          ? (recordFailures[0]?.error ?? null)
          : null;

      // `isFetched` (not `!isPending`) so a failed check whose request the
      // table is retrying doesn't pull the tabs, and the table, back out.
      if (!results.every(({ isFetched }) => isFetched)) return { error };

      const enabledTabs = VISIBLE_TABS.filter(
        (_, index) =>
          finds(records[index]) ||
          finds(registrations[index]) ||
          (hasFailed(records[index]) && hasFailed(registrations[index])),
      );

      return {
        defaultTab:
          VISIBLE_TABS.find((_, index) => finds(records[index])) ??
          enabledTabs[0],
        enabledTabs,
        error,
      };
    },
    // Every tab's records check, then every tab's registration check, so tab
    // `index` reads `results[index]` and `results[VISIBLE_TABS.length + index]`.
    queries: [
      ...VISIBLE_TABS.map(({ serviceTypes }) => {
        const isStatementsMode = usesStatementsApi(serviceTypes);
        const scope = { clientId, serviceTypes, workspaceId };
        const firstPage = firstTabListPage(isStatementsMode);
        const options = isStatementsMode
          ? queryOptionsGetStatementsPaginated(
              workspaceId ? tabStatementsParams(scope, firstPage) : undefined,
            )
          : queryOptionsGetInvoicesPaginated(
              workspaceId ? tabInvoicesParams(scope, firstPage) : undefined,
            );

        return { ...options, refetchInterval: false as const };
      }),
      ...VISIBLE_TABS.map(({ serviceTypes }) => ({
        ...queryOptionsGetTitlesPaginated({
          ownerClientIds: [clientId],
          pageSize: 1,
          serviceTypes,
          workspaceId,
        }),
        // `useQueries` gives each new key its own observer, so `titlesApi`'s
        // `keepPreviousData` never carries one client's titles to the next;
        // cleared anyway so this doesn't depend on that. The `isFetched` gate
        // is what keeps a previous client's tabs off screen.
        placeholderData: undefined,
        refetchInterval: false as const,
        // A failure falls back to the records check, so there is nothing
        // worth holding the skeleton for a retry over.
        retry: false,
      })),
    ],
  });
